import { Loader2, DownloadCloud } from "lucide-react";
import { VenueVideoPicker } from "@/components/admin/VenueVideoPicker";

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-[10px] tracking-overline uppercase text-[#8A8782] mb-2">
        {label}{" "}
        {hint && <span className="normal-case text-[#B0ADA6]">{hint}</span>}
      </label>
      {children}
    </div>
  );
}

const inputCls =
  "w-full border-b border-[#D1CEC2] bg-transparent text-lg py-3 focus:border-[#2F2E2E] outline-none";

export function QuoteDetailsFields({ quote, setQuote, onBrevoLookup, lookingUp }) {
  const set = (patch) => setQuote({ ...quote, ...patch });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      <Field label="Partner 1 First Name">
        <input
          type="text"
          value={quote.partner1_name}
          onChange={(e) => set({ partner1_name: e.target.value })}
          data-testid="partner1-name-input"
          placeholder="Mario"
          className={inputCls}
        />
      </Field>
      <Field label="Partner 2 First Name">
        <input
          type="text"
          value={quote.partner2_name}
          onChange={(e) => set({ partner2_name: e.target.value })}
          data-testid="partner2-name-input"
          placeholder="Phillip"
          className={inputCls}
        />
      </Field>
      <div className="md:col-span-2">
        <Field label="Client Email (used for retrieval & Brevo send)">
          <div className="flex items-end gap-3">
            <input
              type="email"
              value={quote.client_email}
              onChange={(e) => set({ client_email: e.target.value })}
              data-testid="client-email-input"
              placeholder="couple@example.com"
              className="flex-1 border-b border-[#D1CEC2] bg-transparent text-lg py-3 focus:border-[#2F2E2E] outline-none"
            />
            <button
              type="button"
              onClick={onBrevoLookup}
              disabled={lookingUp}
              data-testid="brevo-lookup-btn"
              className="inline-flex items-center gap-2 border border-[#2F2E2E] text-[#2F2E2E] hover:bg-[#2F2E2E] hover:text-white px-4 py-3 text-[11px] tracking-overline uppercase font-medium transition-colors disabled:opacity-60 whitespace-nowrap"
            >
              {lookingUp ? (
                <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
              ) : (
                <DownloadCloud className="w-4 h-4" strokeWidth={1.5} />
              )}
              Pull from Brevo
            </button>
          </div>
          <p className="text-xs text-[#8A8782] mt-2">
            Looks up the Brevo contact and auto-fills partner names, venue and wedding date.
          </p>
        </Field>
      </div>
      <Field label="Wedding Date" hint="(shown on the quote hero)">
        <input
          type="date"
          value={quote.wedding_date || ""}
          onChange={(e) => set({ wedding_date: e.target.value })}
          data-testid="wedding-date-input"
          className={inputCls}
        />
      </Field>
      <Field label="Venue Name" hint="(optional)">
        <input
          type="text"
          value={quote.venue_name || ""}
          onChange={(e) => set({ venue_name: e.target.value })}
          data-testid="venue-name-input"
          placeholder="e.g. Gaynes Park"
          className={inputCls}
        />
      </Field>
      <div className="md:col-span-2">
        <VenueVideoPicker
          venueName={quote.venue_name}
          urls={quote.venue_video_urls}
          onChange={(urls) => set({ venue_video_urls: urls })}
        />
      </div>
      <Field label="Deposit" hint="(optional)">
        <div className="flex items-center gap-3">
          <select
            value={quote.deposit_type || ""}
            onChange={(e) => set({ deposit_type: e.target.value })}
            data-testid="deposit-type-select"
            className="border-b border-[#D1CEC2] bg-transparent text-base py-3 focus:border-[#2F2E2E] outline-none"
          >
            <option value="">No deposit shown</option>
            <option value="percent">% of package price</option>
            <option value="fixed">Fixed amount (£)</option>
          </select>
          {quote.deposit_type && (
            <input
              type="number"
              min="0"
              step="0.01"
              value={quote.deposit_value ?? ""}
              onChange={(e) => set({ deposit_value: e.target.value })}
              data-testid="deposit-value-input"
              placeholder={quote.deposit_type === "percent" ? "e.g. 25" : "e.g. 1000"}
              className="w-32 border-b border-[#D1CEC2] bg-transparent text-lg py-3 tabular-nums focus:border-[#2F2E2E] outline-none"
            />
          )}
        </div>
        <p className="text-xs text-[#8A8782] mt-2">
          Shown under each package: "Secure your date with just £X" plus a note about interest-free monthly payments for the balance.
        </p>
      </Field>
      <Field label="Quote Expiry">
        <select
          value={quote.expiry_type || "7_days"}
          onChange={(e) => set({ expiry_type: e.target.value })}
          data-testid="expiry-type-select"
          className="border-b border-[#D1CEC2] bg-transparent text-base py-3 focus:border-[#2F2E2E] outline-none w-full"
        >
          <option value="7_days">7 days from sending</option>
          <option value="end_of_month">End of current month</option>
          <option value="no_expiry">No expiry (stays open)</option>
          <option value="custom">Custom date</option>
        </select>
        {quote.expiry_type === "custom" && (
          <input
            type="date"
            value={quote.expiry_date || ""}
            onChange={(e) => set({ expiry_date: e.target.value })}
            data-testid="expiry-date-input"
            className="mt-3 border-b border-[#D1CEC2] bg-transparent text-base py-2 focus:border-[#2F2E2E] outline-none w-full"
          />
        )}
        <p className="text-xs text-[#8A8782] mt-2">
          {quote.expiry_type === "no_expiry"
            ? "The quote stays open with no countdown or expiry."
            : 'Expires at 11:59pm UK on the final day. "End of current month" means the last day of the month the quote is sent in.'}
        </p>
      </Field>
      <div className="md:col-span-2 pt-2 space-y-4">
        <label className="flex items-center gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={!!quote.video_booth_offer}
            onChange={(e) => set({ video_booth_offer: e.target.checked })}
            data-testid="video-booth-offer-checkbox"
            className="w-4 h-4 accent-[#2F2E2E]"
          />
          <span className="text-sm text-[#1F1E1D]">
            Include the <strong>Video Messages Guestbook offer</strong>{" "}
            <span className="text-[#8A8782]">
              (FREE if they secure their date within 7 days, usually £499)
            </span>
          </span>
        </label>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={!!quote.second_photographer_offer}
              onChange={(e) => set({ second_photographer_offer: e.target.checked })}
              data-testid="second-photographer-offer-checkbox"
              className="w-4 h-4 accent-[#2F2E2E]"
            />
            <span className="text-sm text-[#1F1E1D]">
              Offer the <strong>Second Photographer add-on</strong>{" "}
              <span className="text-[#8A8782]">(they can add it when accepting)</span>
            </span>
          </label>
          {quote.second_photographer_offer && (
            <span className="flex items-center gap-1 text-sm text-[#5C5A56]">
              +£
              <input
                type="number"
                step="0.01"
                value={quote.second_photographer_offer_price ?? 450}
                onChange={(e) => set({ second_photographer_offer_price: parseFloat(e.target.value) || 0 })}
                data-testid="second-photographer-offer-price"
                className="w-24 border-b border-[#D1CEC2] bg-transparent py-1 tabular-nums focus:border-[#2F2E2E] outline-none"
              />
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={!!quote.documentary_edit_offer}
              onChange={(e) => set({ documentary_edit_offer: e.target.checked })}
              data-testid="documentary-edit-offer-checkbox"
              className="w-4 h-4 accent-[#2F2E2E]"
            />
            <span className="text-sm text-[#1F1E1D]">
              Offer <strong>The Documentary Edit add-on</strong>{" "}
              <span className="text-[#8A8782]">(they can add it when accepting)</span>
            </span>
          </label>
          {quote.documentary_edit_offer && (
            <span className="flex items-center gap-1 text-sm text-[#5C5A56]">
              +£
              <input
                type="number"
                step="0.01"
                value={quote.documentary_edit_offer_price ?? 199}
                onChange={(e) => set({ documentary_edit_offer_price: parseFloat(e.target.value) || 0 })}
                data-testid="documentary-edit-offer-price"
                className="w-24 border-b border-[#D1CEC2] bg-transparent py-1 tabular-nums focus:border-[#2F2E2E] outline-none"
              />
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={!!quote.prewedding_shoot_offer}
              onChange={(e) => set({ prewedding_shoot_offer: e.target.checked })}
              data-testid="prewedding-shoot-offer-checkbox"
              className="w-4 h-4 accent-[#2F2E2E]"
            />
            <span className="text-sm text-[#1F1E1D]">
              Include a free <strong>Pre-Wedding Couple Photoshoot</strong>{" "}
              <span className="text-[#8A8782]">(60 minutes, usually £249, included free)</span>
            </span>
          </label>
          {quote.prewedding_shoot_offer && (
            <span className="flex items-center gap-1 text-sm text-[#5C5A56]">
              usually £
              <input
                type="number"
                step="0.01"
                value={quote.prewedding_shoot_offer_price ?? 249}
                onChange={(e) => set({ prewedding_shoot_offer_price: parseFloat(e.target.value) || 0 })}
                data-testid="prewedding-shoot-offer-price"
                className="w-24 border-b border-[#D1CEC2] bg-transparent py-1 tabular-nums focus:border-[#2F2E2E] outline-none"
              />
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={!!quote.content_creator_offer}
              onChange={(e) => set({ content_creator_offer: e.target.checked })}
              data-testid="content-creator-offer-checkbox"
              className="w-4 h-4 accent-[#2F2E2E]"
            />
            <span className="text-sm text-[#1F1E1D]">
              Offer the <strong>Content Creator</strong> add-on{" "}
              <span className="text-[#8A8782]">(bookable with any collection, usually £995)</span>
            </span>
          </label>
          {quote.content_creator_offer && (
            <span className="flex items-center gap-1 text-sm text-[#5C5A56]">
              +£
              <input
                type="number"
                step="0.01"
                value={quote.content_creator_offer_price ?? 695}
                onChange={(e) => set({ content_creator_offer_price: parseFloat(e.target.value) || 0 })}
                data-testid="content-creator-offer-price"
                className="w-24 border-b border-[#D1CEC2] bg-transparent py-1 tabular-nums focus:border-[#2F2E2E] outline-none"
              />
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={!!quote.no_socials}
              onChange={(e) => set({ no_socials: e.target.checked })}
              data-testid="no-socials-checkbox"
              className="w-4 h-4 accent-[#8A3D3D]"
            />
            <span className="text-sm text-[#1F1E1D]">
              <strong className={quote.no_socials ? "text-[#8A3D3D]" : ""}>No socials</strong>{" "}
              <span className="text-[#8A8782]">
                (couple does not want their films or photos on social media, sent to the rota app as NO SOCIALS)
              </span>
            </span>
          </label>
        </div>
      </div>
    </div>
  );
}
